import React from 'react';
import { 
    Checkbox, 
    Select, 
    Button, 
    Space, 
    Divider, 
    Card, 
    Tag, 
    Typography,
    Tooltip
} from 'antd';
import { 
    PlusOutlined, 
    DeleteOutlined, 
    ClockCircleOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    GlobalOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const DayTimeSlots = ({ 
    selectedDays = [], 
    onDaysChange, 
    dayTimeSlots = {}, 
    onTimeSlotsChange, 
    errors = {} 
}) => {
    const days = [
        { key: 'mon', label: 'Monday' },
        { key: 'tue', label: 'Tuesday' },
        { key: 'wed', label: 'Wednesday' },
        { key: 'thu', label: 'Thursday' },
        { key: 'fri', label: 'Friday' },
        { key: 'sat', label: 'Saturday' },
        { key: 'sun', label: 'Sunday' }
    ];

    const allTimes = [
        { value: "0000", label: "12:00 AM" },
        { value: "0030", label: "12:30 AM" },
        { value: "0100", label: "1:00 AM" },
        { value: "0130", label: "1:30 AM" },
        { value: "0200", label: "2:00 AM" },
        { value: "0230", label: "2:30 AM" },
        { value: "0300", label: "3:00 AM" },
        { value: "0330", label: "3:30 AM" },
        { value: "0400", label: "4:00 AM" },
        { value: "0430", label: "4:30 AM" },
        { value: "0500", label: "5:00 AM" },
        { value: "0530", label: "5:30 AM" },
        { value: "0600", label: "6:00 AM" },
        { value: "0630", label: "6:30 AM" },
        { value: "0700", label: "7:00 AM" },
        { value: "0730", label: "7:30 AM" },
        { value: "0800", label: "8:00 AM" },
        { value: "0830", label: "8:30 AM" },
        { value: "0900", label: "9:00 AM" },
        { value: "0930", label: "9:30 AM" },
        { value: "1000", label: "10:00 AM" },
        { value: "1030", label: "10:30 AM" },
        { value: "1100", label: "11:00 AM" },
        { value: "1130", label: "11:30 AM" },
        { value: "1200", label: "12:00 PM" },
        { value: "1230", label: "12:30 PM" },
        { value: "1300", label: "1:00 PM" },
        { value: "1330", label: "1:30 PM" },
        { value: "1400", label: "2:00 PM" },
        { value: "1430", label: "2:30 PM" },
        { value: "1500", label: "3:00 PM" },
        { value: "1530", label: "3:30 PM" },
        { value: "1600", label: "4:00 PM" },
        { value: "1630", label: "4:30 PM" },
        { value: "1700", label: "5:00 PM" },
        { value: "1730", label: "5:30 PM" },
        { value: "1800", label: "6:00 PM" },
        { value: "1830", label: "6:30 PM" },
        { value: "1900", label: "7:00 PM" },
        { value: "1930", label: "7:30 PM" },
        { value: "2000", label: "8:00 PM" },
        { value: "2030", label: "8:30 PM" },
        { value: "2100", label: "9:00 PM" },
        { value: "2130", label: "9:30 PM" },
        { value: "2200", label: "10:00 PM" },
        { value: "2230", label: "10:30 PM" },
        { value: "2300", label: "11:00 PM" },
        { value: "2330", label: "11:30 PM" },
        { value: "2400", label: "12:00 AM (Next Day)" }
    ];

    const handleDayToggle = (dayKey) => {
        const isSelected = selectedDays.includes(dayKey);
        const newSelectedDays = isSelected
            ? selectedDays.filter(day => day !== dayKey)
            : [...selectedDays, dayKey];
        
        onDaysChange(newSelectedDays);
        
        if (!isSelected) {
            // Initializing with present and one slot
            const newDayTimeSlots = { ...dayTimeSlots };
            newDayTimeSlots[dayKey] = [{ start_time: '0900', end_time: '1700' }];
            onTimeSlotsChange(newDayTimeSlots);
        } else {
            const newDayTimeSlots = { ...dayTimeSlots };
            delete newDayTimeSlots[dayKey];
            onTimeSlotsChange(newDayTimeSlots);
        }
    };

    const handleDayTypeChange = (dayKey, type) => {
        const newDayTimeSlots = { ...dayTimeSlots };
        if (type === '24hours') {
            newDayTimeSlots[dayKey] = [{ start_time: '0000', end_time: '2400' }];
        } else if (type === 'closed') {
            newDayTimeSlots[dayKey] = [{ start_time: 'closed', end_time: 'closed' }];
        } else {
            newDayTimeSlots[dayKey] = [{ start_time: '0900', end_time: '1700' }];
        }
        onTimeSlotsChange(newDayTimeSlots);
    };

    const addTimeSlot = (dayKey) => {
        const newDayTimeSlots = { ...dayTimeSlots };
        const daySlots = newDayTimeSlots[dayKey] || [];
        const lastSlot = daySlots[daySlots.length - 1];
        const nextStart = lastSlot ? lastSlot.end_time : '0900';
        newDayTimeSlots[dayKey] = [...daySlots, { start_time: nextStart, end_time: '' }];
        onTimeSlotsChange(newDayTimeSlots);
    };

    const removeTimeSlot = (dayKey, slotIndex) => {
        const newDayTimeSlots = { ...dayTimeSlots };
        const daySlots = newDayTimeSlots[dayKey] || [];
        if (daySlots.length > 1) {
            newDayTimeSlots[dayKey] = daySlots.filter((_, index) => index !== slotIndex);
            onTimeSlotsChange(newDayTimeSlots);
        }
    };

    const handleTimeSlotChange = (dayKey, slotIndex, fieldName, value) => {
        const newDayTimeSlots = { ...dayTimeSlots };
        const newDaySlots = [...(newDayTimeSlots[dayKey] || [])];
        newDaySlots[slotIndex] = { ...newDaySlots[slotIndex], [fieldName]: value };
        newDayTimeSlots[dayKey] = newDaySlots;
        onTimeSlotsChange(newDayTimeSlots);
    };

    const getDayType = (dayKey) => {
        const daySlots = dayTimeSlots[dayKey];
        if (!daySlots || daySlots.length === 0) return 'closed';
        const slot = daySlots[0];
        if (slot.start_time === '0000' && slot.end_time === '2400') return '24hours';
        if (slot.start_time === 'closed' && slot.end_time === 'closed') return 'closed';
        return 'present';
    };

    return (
        <div className="day-time-slots-modern">
            <style>{`
                .day-row {
                    background: #fff;
                    border: 1px solid #edf2f7;
                    border-radius: 12px;
                    padding: 16px;
                    margin-bottom: 12px;
                    transition: all 0.2s ease;
                }
                .day-row.active {
                    border-color: #3182ce;
                    background: #f7fafc;
                }
                .day-label {
                    font-weight: 700;
                    font-size: 14px;
                    color: #2d3748;
                    min-width: 90px;
                }
                .slot-input-group {
                    background: #fff;
                    padding: 8px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .error-text {
                    color: #e53e3e;
                    font-size: 11px;
                    margin-top: 4px;
                }
            `}</style>

            <div className="mb-4">
                <Text strong style={{ fontSize: '15px', color: '#4a5568', display: 'block', marginBottom: '16px' }}>
                    <ClockCircleOutlined /> Set Weekly Availability
                </Text>

                {days.map(day => {
                    const isSelected = selectedDays.includes(day.key);
                    const dayType = getDayType(day.key);
                    const slots = dayTimeSlots[day.key] || [];

                    return (
                        <div key={day.key} className={`day-row ${isSelected ? 'active' : ''}`}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <Space size="middle">
                                    <Checkbox 
                                        checked={isSelected} 
                                        onChange={() => handleDayToggle(day.key)}
                                    />
                                    <span className="day-label">{day.label}</span>
                                </Space>

                                {isSelected && (
                                    <Select 
                                        value={dayType} 
                                        onChange={v => handleDayTypeChange(day.key, v)}
                                        style={{ width: 140 }}
                                        size="small"
                                    >
                                        <Option value="present">Available Slots</Option>
                                        <Option value="24hours">Open 24 Hours</Option>
                                        <Option value="closed">Closed</Option>
                                    </Select>
                                )}

                                {!isSelected && <Text type="secondary" style={{ fontSize: '12px' }}>Not selected</Text>}
                            </div>

                            {isSelected && dayType === 'present' && (
                                <div className="mt-3 ps-4">
                                    {slots.map((slot, idx) => (
                                        <div key={idx} className="mb-2">
                                            <Space align="center" wrap>
                                                <div className="slot-input-group">
                                                    <Select
                                                        placeholder="Start"
                                                        value={slot.start_time}
                                                        onChange={v => handleTimeSlotChange(day.key, idx, 'start_time', v)}
                                                        style={{ width: 110 }}
                                                        bordered={false}
                                                        size="small"
                                                        showSearch
                                                    >
                                                        {allTimes.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                                                    </Select>
                                                    <Text type="secondary">—</Text>
                                                    <Select
                                                        placeholder="End"
                                                        value={slot.end_time}
                                                        onChange={v => handleTimeSlotChange(day.key, idx, 'end_time', v)}
                                                        style={{ width: 110 }}
                                                        bordered={false}
                                                        size="small"
                                                        showSearch
                                                    >
                                                        {allTimes.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                                                    </Select>
                                                </div>
                                                
                                                {idx === 0 ? (
                                                    <Tooltip title="Add Slot">
                                                        <Button 
                                                            type="text" 
                                                            icon={<PlusOutlined />} 
                                                            onClick={() => addTimeSlot(day.key)}
                                                            className="text-success"
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Remove Slot">
                                                        <Button 
                                                            type="text" 
                                                            danger 
                                                            icon={<DeleteOutlined />} 
                                                            onClick={() => removeTimeSlot(day.key, idx)}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Space>
                                            {errors[`${day.key}_time_slot_${idx}_start`] && (
                                                <div className="error-text">{errors[`${day.key}_time_slot_${idx}_start`]}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {isSelected && dayType === '24hours' && (
                                <div className="mt-2 ps-4">
                                    <Tag icon={<GlobalOutlined />} color="success">Open 24/7</Tag>
                                </div>
                            )}

                            {isSelected && dayType === 'closed' && (
                                <div className="mt-2 ps-4">
                                    <Tag icon={<CloseCircleFilled />} color="error">Clinic Closed</Tag>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {errors.selected_days && <div className="alert alert-danger py-2" style={{ fontSize: '13px' }}>{errors.selected_days}</div>}
        </div>
    );
};

export default DayTimeSlots;

